import {
  Button,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { Fragment, useState } from 'react';
import { RHFEditor, RHFSelect } from 'src/components/hook-form';
import SvgColor from 'src/components/svg-color';
import PropTypes from 'prop-types';
import { useFieldArray } from 'react-hook-form';
import Iconify from 'src/components/iconify';
import GenerateWithAiDialog from './generate-with-ai-dialog';
import TestCases from './test-cases';
import Languages from './languages';

const TIME_LIMITS = [
  { value: 5, label: '5 minutes' },
  { value: 10, label: '10 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 20, label: '20 minutes' },
  { value: 25, label: '25 minutes' },
  { value: 30, label: '30 minutes' },
];

export default function CodingAssessment({
  methods,
  index: codingAssessmentIndex,
  removeCodingAssessment,
}) {
  // variables
  const parentFieldName = `codingAssessments.${codingAssessmentIndex}`;

  // hooks
  const { fields: codingQuestionFields } = useFieldArray({
    control: methods.control,
    name: `${parentFieldName}.questions`,
  });

  // states
  const [openGenerateWithAiDialog, setOpenGenerateWithAiDialog] =
    useState(false);

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb="30px"
      >
        <Typography variant="subheading1" fontSize={18}>
          Coding Assessment
        </Typography>
        <IconButton
          onClick={() => {
            removeCodingAssessment(codingAssessmentIndex);
          }}
        >
          <Iconify
            icon="tabler:trash"
            color={theme => theme.palette.background.primaryRed1}
          />
        </IconButton>
      </Stack>

      <Stack>
        <Stack spacing="20px">
          {codingQuestionFields.map((field, index) => {
            const rootFieldName = `${parentFieldName}.questions.${index}`;
            const questionFieldName = `${rootFieldName}.text`;

            return (
              <Fragment key={index}>
                <Stack spacing="34px">
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="subheading2" fontSize={16}>
                      Question
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{
                        width: '170px',
                        whiteSpace: 'nowrap',
                        color: theme => theme.palette.content.primaryBlack1,
                        background: theme =>
                          theme.palette.background.primaryGreen3,
                        '&:hover': {
                          background: theme =>
                            theme.palette.background.primaryBlack1,
                          color: theme => theme.palette.common.white,
                        },
                      }}
                      startIcon={
                        <SvgColor src="/assets/icons/job/ic_star_twinkle.svg" />
                      }
                      onClick={() => setOpenGenerateWithAiDialog(true)}
                    >
                      Generate with AI
                    </Button>
                  </Stack>
                  <Stack>
                    <RHFEditor
                      simple
                      id={`editor-${index}`}
                      name={`${questionFieldName}`}
                    />
                  </Stack>
                </Stack>
                <TestCases parentFieldName={rootFieldName} methods={methods} />
                <Stack spacing="12px">
                  <Typography variant="subheading1" fontSize={18}>
                    Time Limit
                  </Typography>
                  <RHFSelect name={`${rootFieldName}.timeLimit`}>
                    {TIME_LIMITS.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                </Stack>
              </Fragment>
            );
          })}

          <Divider />

          <Languages methods={methods} parentFieldName={parentFieldName} />
        </Stack>
      </Stack>

      <GenerateWithAiDialog
        open={openGenerateWithAiDialog}
        setOpen={setOpenGenerateWithAiDialog}
        methods={methods}
      />
    </Stack>
  );
}

CodingAssessment.propTypes = {
  methods: PropTypes.object,
  index: PropTypes.number,
  removeCodingAssessment: PropTypes.func,
};
